import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, ShieldCheck, Timer, Paperclip, X } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { Field, inputCls, VoltButton, SuccessPanel } from '../components/site/FormBits';
import { CATEGORIES, SERVICES, CATEGORY_SERVICE_MAP, BUDGET_RANGES, COUNTRIES, SERVICE_GROUPS, DELIVERY_MODES } from '../data/site';
import { API, track } from '../lib/api';
import { useSEO } from '../lib/seo';

const props = [
  { icon: FileText, title: 'Post once', text: 'One RFQ with drawings and specs attached. No repeating yourself across five phone calls.' },
  { icon: ShieldCheck, title: 'Vetted providers', text: 'Every provider is checked as they join — business registry, safety credentials, insurance — before being matched to your RFQ.' },
  { icon: Timer, title: 'No procurement chase', text: "Target: 3–5 matched providers in 24–48 hours. If suitable providers aren't available in your trade yet, the founder tells you honestly and keeps sourcing." },
];

const Buyers = () => {
  useSEO({
    title: 'Post an RFQ — Quotes from Vetted Providers, Free | Vox Meridian',
    description: 'Post your RFQ once and get matched with 3–5 verified providers in your market, then track quotes and decisions in your private dashboard.',
    path: '/buyers',
  });
  const [params] = useSearchParams();
  const preset = params.get('category');
  const presetCat = CATEGORIES.find((c) => c.slug === preset);
  const presetTarget = presetCat ? CATEGORY_SERVICE_MAP[presetCat.slug] || presetCat.name : preset;
  const presetService = SERVICES.find((s) => s.name.toLowerCase() === (presetTarget || '').toLowerCase());
  const presetCountry = params.get('country');
  const presetGroup = presetService
    ? (SERVICE_GROUPS.find((g) => g.trades.includes(presetService.name)) || SERVICE_GROUPS[0]).name
    : SERVICE_GROUPS[0].name;
  const [group, setGroup] = useState(presetGroup);
  const activeTrades = SERVICES.filter((s) =>
    (SERVICE_GROUPS.find((g) => g.name === group) || SERVICE_GROUPS[0]).trades.includes(s.name)
  );
  const [form, setForm] = useState({
    category: presetService ? presetService.name : '',
    country: COUNTRIES.some((c) => c.code === presetCountry) ? presetCountry : 'SG',
    scope: '',
    budget_range: '',
    deadline: '',
    company: '',
    contact_name: '',
    email: '',
    phone: '',
    delivery_mode: 'on_site',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      await axios.post(`${API}/rfqs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      track('conversion', { type: 'buyer_rfq', category: form.category });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="buyers-page">
      <PageHero
        eyebrow="For buyers"
        lines={[<>Post once.</>, <span key="l2">Quotes from <span className="text-volt">vetted providers.</span></span>]}
        sub="Stop the procurement chase. Post your RFQ with drawings and specs — the founder reviews and matches it with verified providers in your country, targeting 24–48 hours, subject to provider availability. Free during the pilot."
      />

      <section className="border-b border-line bg-ink">
        <div className="mx-auto grid max-w-7xl sm:grid-cols-3">
          {props.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1} className={`border-line p-8 sm:p-10 ${i > 0 ? 'border-t sm:border-l sm:border-t-0' : ''}`}>
              <p.icon size={28} className="text-volt" />
              <h3 className="mt-4 font-display text-base font-bold text-parchment">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink bg-grid">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter no="01" label="Submit an RFQ" title="Two minutes. Done." />
              <Reveal delay={0.1}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-steel">
                  Tell us the category, scope, budget band and deadline. Attach drawings or specs if
                  you have them — PDF, DWG, STEP or ZIP up to 15MB. The sharper the scope, the sharper
                  the quotes.
                </p>
                <div className="mt-8 border border-line bg-surface p-6">
                  <p className="mono-label text-volt">Pilot phase</p>
                  <p className="mt-2 text-sm leading-relaxed text-steel">
                    Buyers pay nothing. Every RFQ is reviewed and matched by hand — you will hear from
                    the founder directly, not a bot.
                  </p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              {done ? (
                <SuccessPanel
                  testId="rfq-success"
                  title="RFQ received."
                  lines={[
                    'A confirmation email is on its way to you.',
                    'The founder aims to match 3–5 providers within 24–48 hours — and will tell you honestly if your trade needs more sourcing time.',
                  ]}
                />
              ) : (
                <form data-testid="rfq-form" onSubmit={submit} className="space-y-6 border border-line bg-surface p-8 sm:p-10">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Service category" htmlFor="b-group">
                      <select id="b-group" data-testid="rfq-group-select" className={inputCls} value={group} onChange={(e) => { setGroup(e.target.value); setForm({ ...form, category: '' }); }}>
                        {SERVICE_GROUPS.map((g) => (
                          <option key={g.name} value={g.name}>{g.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Sub-service" htmlFor="b-cat">
                      <select id="b-cat" data-testid="rfq-category-select" required className={inputCls} value={form.category} onChange={set('category')}>
                        <option value="" disabled>Select sub-service</option>
                        {activeTrades.map((s) => (
                          <option key={s.name} value={s.name} label={`${s.name} — ${s.examples}`} />
                        ))}
                      </select>
                    </Field>
                    <Field label="Where is the work?" htmlFor="b-country">
                      <select id="b-country" data-testid="rfq-country-select" className={inputCls} value={form.country} onChange={set('country')}>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Delivery mode" htmlFor="b-delivery" hint="This controls whether local or cross-border providers can match.">
                      <select id="b-delivery" data-testid="rfq-delivery-mode-select" className={inputCls} value={form.delivery_mode} onChange={set('delivery_mode')}>
                        {DELIVERY_MODES.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Budget range (SGD equivalents)" htmlFor="b-budget">
                      <select id="b-budget" data-testid="rfq-budget-select" required className={inputCls} value={form.budget_range} onChange={set('budget_range')}>
                        <option value="" disabled>Select range</option>
                        {BUDGET_RANGES.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Scope of work" htmlFor="b-scope" hint="What needs doing, what went wrong (symptoms), equipment make and model, quantities, materials, site access — the detail that gets you comparable quotes fast.">
                    <textarea id="b-scope" data-testid="rfq-scope-textarea" required rows={5} className={`${inputCls} resize-y`} value={form.scope} onChange={set('scope')} placeholder="e.g. Machine 20 pcs of bracket per attached drawing, 6061-T6, ±0.05mm, anodised black…" />
                  </Field>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Required by" htmlFor="b-deadline">
                      <input id="b-deadline" data-testid="rfq-deadline-input" required type="date" className={`${inputCls} [color-scheme:dark]`} value={form.deadline} onChange={set('deadline')} />
                    </Field>
                    <Field label="Company" htmlFor="b-company">
                      <input id="b-company" data-testid="rfq-company-input" required className={inputCls} value={form.company} onChange={set('company')} placeholder="Your company Pte Ltd" />
                    </Field>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Contact person" htmlFor="b-name">
                      <input id="b-name" data-testid="rfq-name-input" required className={inputCls} value={form.contact_name} onChange={set('contact_name')} placeholder="Full name" />
                    </Field>
                    <Field label="Phone (optional)" htmlFor="b-phone">
                      <input id="b-phone" data-testid="rfq-phone-input" className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+65 9123 4567" />
                    </Field>
                  </div>
                  <Field label="Work email" htmlFor="b-email">
                    <input id="b-email" data-testid="rfq-email-input" required type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@company.sg" />
                  </Field>
                  <Field label="Drawings / specs (optional)" htmlFor="b-file" hint="PDF, DWG, DXF, STEP, ZIP or images · max 15MB">
                    <input id="b-file" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    {file ? (
                      <div className="flex items-center justify-between border border-volt/50 bg-volt/5 px-4 py-3" data-testid="rfq-file-attached">
                        <span className="flex items-center gap-2 text-sm text-parchment">
                          <Paperclip size={15} className="text-volt" /> {file.name}
                        </span>
                        <button type="button" data-testid="rfq-file-remove" onClick={() => setFile(null)} className="text-steel hover:text-parchment">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="b-file" data-testid="rfq-file-dropzone" className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line px-4 py-6 text-sm text-steel transition-colors duration-200 hover:border-volt/60 hover:text-parchment">
                        <Paperclip size={16} /> Attach a file
                      </label>
                    )}
                  </Field>
                  <VoltButton type="submit" data-testid="rfq-submit-button" disabled={submitting} className="w-full">
                    {submitting ? 'Submitting…' : 'Submit RFQ — free during pilot'}
                  </VoltButton>
                  <p className="text-center text-xs leading-relaxed text-steel/50">
                    Your details and drawings are shared only with the 3–5 providers matched to quote.
                    Confidential handling on request — {`hello@voxmeridian.com`}.{' '}
                    <a href="/privacy" data-testid="rfq-privacy-link" className="underline transition-colors duration-200 hover:text-volt">Privacy</a>
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Buyers;
